import React, { useState } from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/glyph/add';
import ChevronUpIcon from '@atlaskit/icon/glyph/hipchat/chevron-up';
import Popup from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize, fontSize } from '@atlassian/jira-common-styles/src';
import { useIntl } from '@atlassian/jira-intl';

import type { ui_softwareReleasesApprovers_Approvers$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesApprovers_Approvers.graphql';
import { RIGHT_SIDEBAR_WIDTH } from '@atlassian/jira-software-releases-version-detail-shared';
import { LimitReachedError } from '../common/ui/limit-reached-error';
import { NoPermissionError } from '../common/ui/no-permission-error';
import { VersionApproversCardExperienceSuccess } from '../services/experience-tracker';
import { ApproverSelector } from './approver-selector';
import { APPROVERS_SHOW_MORE_THRESHOLD } from './constants';
import messages from './messages';
import { TaskApprover } from './task-approver';

interface Props {
	jiraVersion: ui_softwareReleasesApprovers_Approvers$key;
}
export const Approvers = ({ jiraVersion }: Props) => {
	const { formatMessage } = useIntl();
	const [isShowingAllApprovers, setIsShowingAllApprovers] = useState(false);
	const version = useFragment(
		graphql`
			fragment ui_softwareReleasesApprovers_Approvers on JiraVersion {
				canAddAndRemoveIssues
				...approverSelector_softwareReleasesApprovers
				approvers(first: 100) @required(action: THROW) @connection(key: "version_approvers") {
					edges @required(action: THROW) {
						node {
							id

							...taskApprover_softwareReleasesApprovers_jiraVersionApprover
						}
					}
				}
				...taskApprover_softwareReleasesApprovers_jiraVersion
			}
		`,
		jiraVersion,
	);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const allApprovers = version.approvers.edges.map((edge) => edge?.node).filter(Boolean);
	const collapsedApproversList = allApprovers.slice(0, APPROVERS_SHOW_MORE_THRESHOLD);
	const moreApproversCount = allApprovers.length - APPROVERS_SHOW_MORE_THRESHOLD;
	const moreApproversCountLabel = `+${moreApproversCount}`;
	const canShowMoreApprovers = allApprovers.length > APPROVERS_SHOW_MORE_THRESHOLD;
	const showMoreMessage = isShowingAllApprovers
		? formatMessage(messages.showLess)
		: formatMessage(messages.showMore);
	const popupContent =
		allApprovers.length >= 100 && version.canAddAndRemoveIssues ? (
			<LimitReachedError />
		) : (
			<NoPermissionError />
		);

	return (
		<ApproversCardWrapper>
			<VersionApproversCardExperienceSuccess />
			<Header>
				<ApproversTitle>{formatMessage(messages.approversTitle)}</ApproversTitle>
				{version.canAddAndRemoveIssues && allApprovers.length < 100 ? (
					<ApproverSelector jiraVersion={version} numberOfApprovers={allApprovers.length} />
				) : (
					<Popup
						content={() => popupContent}
						isOpen={isPopupOpen}
						autoFocus={false}
						onClose={() => setIsPopupOpen(false)}
						placement="bottom-end"
						trigger={(triggerProps) => (
							<Button
								{...triggerProps}
								onClick={() => setIsPopupOpen(true)}
								appearance="subtle"
								iconBefore={<AddIcon size="small" label="" />}
							/>
						)}
					/>
				)}
			</Header>
			{allApprovers.length === 0 && (
				<EmptyStateMessage>{formatMessage(messages.noApproversMessage)}</EmptyStateMessage>
			)}
			<List>
				{(allApprovers.length < APPROVERS_SHOW_MORE_THRESHOLD || isShowingAllApprovers
					? allApprovers
					: collapsedApproversList
				).map((node) =>
					node == null ? null : (
						<TaskApprover key={node.id} jiraVersionApprover={node} jiraVersion={version} />
					),
				)}
			</List>
			{canShowMoreApprovers && (
				<ExpandableListButton onClick={() => setIsShowingAllApprovers(!isShowingAllApprovers)}>
					<ExpandableListLozenge>
						{isShowingAllApprovers ? (
							<ChevronUpIcon size="small" label="" />
						) : (
							moreApproversCountLabel
						)}
					</ExpandableListLozenge>
					<ExpandableListText>{showMoreMessage}</ExpandableListText>
				</ExpandableListButton>
			)}
		</ApproversCardWrapper>
	);
};

const BORDER_SIZE = 1;
const PADDING_SIZE = gridSize * 3;
const WIDTH_OFFSET = (PADDING_SIZE + BORDER_SIZE) * 2;

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ApproversCardWrapper = styled.div({
	gap: token('space.200', '16px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxWidth: `${RIGHT_SIDEBAR_WIDTH - WIDTH_OFFSET}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	maxHeight: gridSize * 49,
	overflow: 'auto',
	padding: token('space.300', '24px'),
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const Header = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
});

// eslint-disable-next-line jira/styled/no-styled-export, @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const ApproversTitle = styled.div({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	fontSize: `${fontSize}px`,
	fontWeight: 600,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmptyStateMessage = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.subtlest', colors.N700),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	fontSize: '14px',
	paddingTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	listStyle: 'none',
	padding: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableListText = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableListLozenge = styled.div({
	padding: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 3}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.neutral', colors.N20A),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 4}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N300),
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandableListButton = styled.button({
	background: 'none',
	border: 'none',
	padding: `${token('space.100', '8px')}`,
	cursor: 'pointer',
	display: 'flex',
	gap: token('space.200', '16px'),
	alignItems: 'center',
	justifyContent: 'center',
});
